import { Box, Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import flagGB from 'src/assets/images/flags/gb.png';
import flagNO from 'src/assets/images/flags/no.png';
import {
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { Header2 } from 'src/components/shared/Typography/Header2';
import { Header3 } from 'src/components/shared/Typography/Header3';
import { useReduxDispatch } from 'src/store';
import { alertSuccess } from 'src/store/alerts/alerts.actions';
import { UserDto } from 'src/store/users/user';
import { setUserLanguage } from 'src/store/users/users.actions';
import { UserApi } from 'src/store/users/users.api';
import { Language } from 'src/store/users/users.reducer';
import {
  required,
  runValidations,
  validationRunner,
  validEmail,
  validPassword
} from 'src/utils/validation';
import styled from 'styled-components';

type Props = {
  user: Nullable<UserDto>;
  onCancel: () => void;
};

export const ProfileForm: React.FC<Props> = props => {
  const { onCancel } = props;

  const [user] = useState<UserDto>(props.user || ({} as UserDto));

  const { t, i18n } = useTranslation();
  const dispatch = useReduxDispatch();

  const onSubmit = async (user: UserDto) => {
    const response = await UserApi.edit(user.id, user);

    if (response.ok) {
      dispatch(alertSuccess(t('user.updated')));
    }
  };

  const onChangeLanguage = (language: Language) => {
    dispatch(setUserLanguage(language));
    i18n.changeLanguage(language);
    dispatch(alertSuccess(`${t('language.changed')} ${language}`));
    onCancel();
  };

  const validate = (values: UserDto) => {
    const validationRunners = [
      validationRunner('firstName', t('props.firstname'), required),
      validationRunner('lastName', t('props.lastname'), required),
      validationRunner('email', t('props.email'), required, validEmail),
      validationRunner('username', t('props.username'), required),
      validationRunner('password', t('props.password'), validPassword)
    ];
    return runValidations(values, validationRunners);
  };

  return (
    <>
      <Formik
        initialValues={user}
        validate={validate}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit
        }) => (
          <Form onSubmit={handleSubmit}>
            <Grid container>
              <Grid item xs={12}>
                <Header2 className="mb-32">{t('profile')}</Header2>
                <InputField
                  name="firstName"
                  label={t('props.firstname')}
                  icon="las la-user"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.firstName}
                  displayError={touched.firstName}
                  disabled
                />

                <InputField
                  name="lastName"
                  label={t('props.lastname')}
                  icon="las la-user"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.lastName}
                  displayError={touched.lastName}
                  disabled
                />
                <InputField
                  name="email"
                  label={t('props.email')}
                  icon="las la-at"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.email}
                  displayError={touched.email}
                  disabled
                />
                <InputField
                  name="username"
                  label={t('props.username')}
                  icon="las la-user"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.username}
                  displayError={touched.username}
                  disabled
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="center" marginTop="2rem">
              <TertiaryButton
                onClick={onCancel}
                label={t('cancel')}
                className="mr-32"
                disabled
              />
              <PrimaryButton type="submit" label={t('save.verb')} disabled />
            </Box>
          </Form>
        )}
      </Formik>
      <Languages>
        <Header3 className="mb-16">{t('language.singular')}</Header3>
        <img
          onClick={() => onChangeLanguage(Language.Norwegian)}
          src={flagNO}
          title={t('language.singular')}
          alt="Norwegian flag"
        />
        <img
          onClick={() => onChangeLanguage(Language.English)}
          src={flagGB}
          title={t('language.singular')}
          alt="English flag"
        />
      </Languages>
    </>
  );
};

const Languages = styled.section`
  position: absolute;
  bottom: 2rem;

  & img {
    cursor: pointer;
    margin-right: 1rem;
    height: 16px;
    width: 24px;
    transform: scale(1);
    transition: all 0.2s;

    &:hover {
      transform: scale(1.2);
    }
  }
`;
